import {
  CircleCheck,
  CircleDashed,
  CircleHelp,
  CircleX,
  LoaderCircle,
  type LucideIcon,
} from "lucide-react";

import {
  type SessionIngestionCounts,
  type SessionIngestionSummary,
} from "@/shared/domain/sessions";
import { type BadgeVariant } from "@/shared/foundation/primitives";

export interface SessionBadge {
  label: string;
  variant: BadgeVariant;
  /** Drawn before the label. */
  icon?: LucideIcon;
  /**
   * Set while the ingestion isn't known: "checking" until it is, "unknown" if
   * it couldn't be loaded.
   */
  placeholder?: "checking" | "unknown";
}

export const IN_PROGRESS_BADGE: SessionBadge = {
  label: "In progress",
  variant: "info",
};

export const COMPLETE_BADGE: SessionBadge = {
  label: "Complete",
  variant: "secondary",
};

/** A complete session's ingestion while it isn't known yet. */
export const CHECKING_BADGE: SessionBadge = {
  label: "Checking…",
  variant: "secondary",
  icon: CircleDashed,
  placeholder: "checking",
};

/** A complete session's ingestion when it couldn't be loaded. */
export const UNKNOWN_BADGE: SessionBadge = {
  label: "Status unknown",
  variant: "secondary",
  icon: CircleHelp,
  placeholder: "unknown",
};

/** A session's file counts and the state they were read in. */
export type SessionIngestionCountsWithState = SessionIngestionCounts &
  Pick<SessionIngestionSummary, "state">;

/**
 * The ingestion badge the session page and the sessions list both show, so
 * they always say the same thing. Until the counts load, a complete session
 * says it's checking, or that its status is unknown if they failed to load;
 * an in-progress one shows nothing.
 */
export function ingestionBadgeFor(
  complete: boolean,
  counts: SessionIngestionCountsWithState | undefined,
  { failed }: { failed: boolean },
): SessionBadge | null {
  if (counts !== undefined) {
    return sessionIngestionBadge(complete, counts);
  }
  if (!complete) {
    return null;
  }
  return failed ? UNKNOWN_BADGE : CHECKING_BADGE;
}

/**
 * The badge beside a session's status saying whether its data is ready, in
 * the words file ingestion labels use.
 *
 * A complete session whose counts were read while it was still in progress
 * shows it's checking. Null for an in-progress session with no file waiting,
 * and for a complete session with nothing to ingest: no file ingested and
 * none waiting.
 */
function sessionIngestionBadge(
  complete: boolean,
  counts: SessionIngestionCountsWithState,
): SessionBadge | null {
  if (complete && counts.state === "in_progress") {
    return CHECKING_BADGE;
  }
  // "Ingesting…" is amber, not the file label's blue, to differ from "In progress".
  if (counts.pending_file_count > 0) {
    return counts.failed_file_count > 0
      ? { label: "Ingestion failed", variant: "error", icon: CircleX }
      : { label: "Ingesting…", variant: "warning", icon: LoaderCircle };
  }
  if (!complete || counts.ingested_file_count === 0) {
    return null;
  }
  const skipped = counts.skipped_file_count;
  return {
    label: skipped > 0 ? `Ingested · ${skipped} not waited for` : "Ingested",
    variant: "success",
    icon: CircleCheck,
  };
}
